import React, { useImperativeHandle, useRef } from "react";
import {
  ExcelExportColumn,
  ExcelExport,
} from "@progress/kendo-react-excel-export";
import { useTranslation } from "react-i18next";
import { ExpandedColumn } from "types/columns";
import { usePaymentPlanPageStoreState } from "store/PaymentPlanPageStore";
import { formatDateWithTime } from "utils/pdf";
import { transformRowsForExcel } from "utils/excel";
import { useAppStoreState } from "store/AppStore";
import { applyRedCellHighlights } from "utils/excelExportHighlights";

// What the page holds a ref to. Saving goes through here so the cells the grid
// shows in red are marked in the workbook before it is written.
export type ExportExcelHandle = { save: () => void };

type Props = {
  columns: ExpandedColumn[];
};

const isExported = (c: ExpandedColumn) =>
  c.isVisible === true && c.key !== "ordinalNumber" && c.key !== "select";

export const ExportExcel = React.forwardRef<ExportExcelHandle, Props>(
  (props, ref) => {
    const { columns } = props;
    const { t } = useTranslation();
    const { taxonomy } = useAppStoreState();
    const { xlsRowItems, cols } = usePaymentPlanPageStoreState();
    const excelExportRef = useRef<ExcelExport>(null);

    // Only these become ExcelExportColumns, i.e. the workbook's column order.
    const exportedColumns = columns
      .filter(isExported)
      .filter((c) => !(c.excel && c.excel.isVisible === false));

    // Read by the stable save() below, so a handle captured by the toolbar
    // never works on stale rows.
    const latest = useRef({ xlsRowItems, exportedColumns });
    latest.current = { xlsRowItems, exportedColumns };

    useImperativeHandle(
      ref,
      () => ({
        save: () => {
          const excelExport = excelExportRef.current;
          const { xlsRowItems, exportedColumns } = latest.current;
          if (!excelExport || !xlsRowItems || xlsRowItems.length === 0) return;
          const workbook = excelExport.workbookOptions();
          applyRedCellHighlights(
            workbook,
            xlsRowItems,
            exportedColumns.map((c) => c.additionalInfo?.redColorFn)
          );
          excelExport.save(workbook);
        },
      }),
      []
    );

    return (
      <ExcelExport
        data={transformRowsForExcel({
          rows: xlsRowItems,
          columns: cols.filter(isExported),
          t,
          taxonomy,
        })}
        ref={excelExportRef}
        fileName={`Payment Plans ${formatDateWithTime(new Date())}.xlsx`}
      >
        {exportedColumns.map((col) => {
          let width = col.width;
          if (col.excel && col.excel.width !== undefined) {
            width = col.excel.width;
          }
          let format = null;
          let align: "center" | "left" | "right" = "center";
          if (col.filter) {
            const filterType = col.filter.type;
            if (filterType === "date") {
              align = "center";
              format = "dd.MM.yyyy";
            } else if (filterType === "number") {
              align = "right";
              format = "#,##0.00";
            } else if (filterType === "boolean") {
              align = "left";
              format = "text";
            } else if (filterType === "taxonomy") {
              align = "left";
              format = "text";
            } else if (filterType === "text") {
              align = "left";
              format = "text";
            }
          }

          return (
            <ExcelExportColumn
              key={col.key}
              field={col.key}
              title={t(col.labelKey)}
              cellOptions={{ format: format }}
              width={width}
              headerCellOptions={{
                background: "#005aa1",
                color: "#fff",
                textAlign: col.align ?? ("center" as any),
              }}
            />
          );
        })}
      </ExcelExport>
    );
  }
);
